<template>
  <div data-testid="radio-dropdown" class="radio-dropdown" :style="`width:${width}`">
    <!-- Radio Button Menu -->
    <v-menu
      data-testid="radio-dropdown__menu"
      v-model="menuOpen"
      :close-on-content-click="false"
      offset-y
      :max-height="maxHeight"
      :nudge-bottom="2"
    >
      <template v-slot:activator="{ on, attrs }">
        <!-- Selected Display with Text Field -->
        <v-text-field
          data-testid="radio-dropdown__selector"
          v-bind="attrs"
          :value="selectedDisplay"
          :placeholder="label"
          :disabled="disabled"
          readonly
            dense
          :error="hasError"
          outlined
          hide-details
          :append-icon="menuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          class="radio-dropdown__selector-field rounded-lg"
          @click="!disabled && !readonly ? menuOpen = true : null"
          @click:append="!disabled && !readonly ? menuOpen = !menuOpen : null"
          prepend-inner-icon="mdi-account-outline"
        ></v-text-field>
      </template>

      <v-card data-testid="radio-dropdown__menu-card" :style="`min-width:${width}`">
        <!-- Search Box (optional) -->
        <div 
          data-testid="radio-dropdown__search-wrapper"
          v-if="searchable && items.length > 5" 
          class="pa-3"
        >
          <v-text-field
            data-testid="radio-dropdown__search-input"
            v-model="searchQuery"
            outlined
            rounded="lg"
            hide-details
            placeholder="Search..."
            :prepend-inner-icon="searchPrefixIcon"
            clearable
            :class="`radio-dropdown__search-field ${roundedClass}`"
            @click:clear="searchQuery = ''"
          ></v-text-field>
        </div>

        <!-- Radio Button List -->
        <v-radio-group
          data-testid="radio-dropdown__radio-group"
          v-model="tempSelected"
          class="radio-dropdown__radio-group ma-0 pa-0"
          hide-details
        >
          <v-list data-testid="radio-dropdown__list" class="radio-dropdown__list">
            <v-list-item
              data-testid="radio-dropdown__list-item"
              v-for="item in filteredItems"
              :key="getItemValue(item)"
              @click="selectItem(item)"
              class="radio-dropdown__list-item"
              :class="{ 'radio-dropdown__list-item--selected': isSelected(item) }"
            >
              <v-list-item-action data-testid="radio-dropdown__list-item-action" class="mr-1">
                <v-radio
                  data-testid="radio-dropdown__radio"
                  :value="getItemValue(item)"
                  :color="isSelected(item) ? 'primary' : 'grey'"
                  :class="{ 'radio-dropdown__radio--unselected': !isSelected(item) }"
                ></v-radio>
              </v-list-item-action>
              <v-list-item-content data-testid="radio-dropdown__list-item-content">
                <v-list-item-title 
                  data-testid="radio-dropdown__list-item-title"
                  :class="{ 'font-weight-bold': isSelected(item) }"
                >
                  <template v-if="isInactive(item)">
                    {{ getItemText(item).replace(' (Inactive)', '') }}
                    <span data-testid="radio-dropdown__inactive-label" class="error--text"> (Inactive)</span>
                  </template>
                  <template v-else>
                    {{ getItemText(item) }}
                  </template>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Empty State -->
            <v-list-item 
              data-testid="radio-dropdown__empty-state"
              v-if="filteredItems.length === 0"
            >
              <v-list-item-content>
                <v-list-item-title data-testid="radio-dropdown__empty-text" class="text-center grey--text">
                  No items found
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-radio-group>

        <!-- Apply Button -->
        <v-divider data-testid="radio-dropdown__divider"></v-divider>
        <div data-testid="radio-dropdown__actions" class="pa-2 d-flex justify-end gap-2">
          <base-button
            data-testid="radio-dropdown__cancel-button"
            secondary
            @click="cancelSelection"
            class=""
            label="Cancel"
          />
          <base-button
            data-testid="radio-dropdown__apply-button"
            primary
            @click="applySelection"
            :disabled="tempSelected === null"
            label="Change Role"
          />
        </div>
      </v-card>
    </v-menu>

    <!-- Error Message -->
    <div 
      data-testid="radio-dropdown__error"
      v-if="hasError && !hidedetails" 
      class="radio-dropdown__error error--text text-caption mt-1"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
/**
 * RadioDropdown Component
 * A dropdown component that displays items as radio buttons with an Apply button
 * Follows project coding standards with proper data-testid attributes
 */

export default {
  name: 'RadioDropdown',

  props: {
    // Value binding
    value: {
      type: [String, Number, Object],
      default: null
    },
    
    // Display options
    label: {
      type: String,
      default: ''
    },
    
    // Items configuration
    items: {
      type: Array,
      default: () => []
    },
    itemtext: {
      type: String,
      default: 'name'
    },
    itemvalue: {
      type: String,
      default: 'value'
    },
    
    // Styling
    width: {
      type: String,
      default: '100%'
    },
    
    // Validation
    error: {
      type: [String, Array],
      default: null
    },
    hidedetails: {
      type: Boolean,
      default: false
    },
    
    // States
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    
    // Features
    searchable: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: true
    },
    
    // Menu options
    maxHeight: {
      type: String,
      default: '400px'
    },
    
    // Search field prefix icon
    searchPrefixIcon: {
      type: String,
      default: 'mdi-magnify'
    },
     roundedClass: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    menuOpen: false,
    tempSelected: null,
    searchQuery: ''
  }),

  computed: {
    /**
     * Get display text for selected item
     */
    selectedDisplay() {
      if (!this.value) return '';
      
      const selectedItem = this.items.find(item => {
        return this.getItemValue(item) === this.value;
      });
      
      if (selectedItem) {
        return this.getItemText(selectedItem);
      }
      
      return this.value;
    },

    /**
     * Filter items based on search query
     */
    filteredItems() {
      if (!this.searchQuery) return this.items;
      
      const query = this.searchQuery.toLowerCase();
      return this.items.filter(item => {
        const text = this.getItemText(item).toLowerCase();
        return text.includes(query);
      });
    },

    /**
     * Check if there's an error
     */
    hasError() {
      if (Array.isArray(this.error)) {
        return this.error.length > 0;
      }
      return !!this.error;
    },

    /**
     * Get error message string
     */
    errorMessage() {
      if (Array.isArray(this.error)) {
        return this.error.join(', ');
      }
      return this.error || '';
    },

    /**
     * Menu width matches selector width
     */
    menuWidth() {
      return this.width;
    }
  },

  methods: {
    /**
     * Get item text for display
     */
    getItemText(item) {
      if (typeof item === 'object') {
        return item[this.itemtext] || '';
      }
      return item || '';
    },

    /**
     * Get item value for comparison
     */
    getItemValue(item) {
      if (typeof item === 'object') {
        return item[this.itemvalue] !== undefined ? item[this.itemvalue] : item[this.itemtext];
      }
      return item;
    },

    /**
     * Select an item (temporary selection)
     */
    selectItem(item) {
      this.tempSelected = this.getItemValue(item);
    },

    /**
     * Apply the selected value
     */
    applySelection() {
      this.$emit('input', this.tempSelected);
      this.$emit('change', this.tempSelected);
      this.menuOpen = false;
    },

    /**
     * Cancel selection and close menu
     */
    cancelSelection() {
      this.tempSelected = this.value;
      this.menuOpen = false;
    },

    /**
     * Check if item is inactive
     */
    isInactive(item) {
      const text = this.getItemText(item);
      return text.toString().includes('(Inactive)');
    },

    /**
     * Check if item is currently selected
     */
    isSelected(item) {
      return this.tempSelected === this.getItemValue(item);
    },

    /**
     * Clear selection
     */
    clear() {
      this.$emit('input', null);
      this.$emit('change', null);
    }
  },

  watch: {
    /**
     * Update temp selection when value changes externally
     */
    value(newVal) {
      if (!this.menuOpen) {
        this.tempSelected = newVal;
      }
    },
    
    /**
     * Reset temp selection when menu opens
     */
    menuOpen(isOpen) {
      if (isOpen) {
        this.tempSelected = this.value;
        this.searchQuery = '';
      }
    }
  },

  beforeDestroy() {
    // Reset component state
    this.menuOpen = false;
    this.tempSelected = null;
    this.searchQuery = '';
  }
};
</script>

<style scoped lang="scss">
.radio-dropdown {
  position: relative;
  
  &__selector {
    cursor: pointer;
    padding: 10px 12px;
    min-height: 40px;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    background: white;
    
    &:hover:not(.radio-dropdown__selector--disabled) {
      border-color: rgba(0, 0, 0, 0.87);
    }
    
    &--disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: rgba(0, 0, 0, 0.03);
    }
    
    &--error {
      border-color: #ff5252 !important;
    }
  }
  
  &__selector-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  
  &__label {
    font-size: 14px;
  }
  
  &__selected-text {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
  }
  
  &__icon {
    transition: transform 0.2s ease;
    
    &--open {
      transform: rotate(180deg);
    }
  }
  
  &__error {
    font-size: 12px;
    padding-left: 12px;
  }
  
  &__list {
    max-height: 300px;
    overflow-y: auto;
  }
  
  &__radio-group {
    width: 100%;
  }
  
  &__search-field {
    ::v-deep .v-input__slot {
      min-height: 44px !important;
    }
    
    ::v-deep .v-text-field__slot input {
      font-size: 14px;
    }
  }
  
  &__list-item {
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
    
    &--selected {
      background-color: rgba(var(--v-primary-base), 0.08);
      
      &:hover {
        background-color: rgba(var(--v-primary-base), 0.12);
      }
    }
  }
  
  &__radio--unselected {
    ::v-deep .v-icon {
      color: rgba(0, 0, 0, 0.38) !important;
    }
  }
}
</style>
